/**
 * Input Validation and Sanitization Utility
 * Comprehensive input validation to prevent injection attacks and ensure data integrity
 */

import { logSecurityEvent, EVENT_TYPES } from './securityLogger';

// Validation rules configuration
export const VALIDATION_RULES = {
  EMAIL: {
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    maxLength: 254,
    minLength: 5
  },
  PASSWORD: {
    minLength: 8,
    maxLength: 128,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true
  },
  USERNAME: {
    pattern: /^[a-zA-Z0-9_-]{3,20}$/,
    maxLength: 20,
    minLength: 3
  },
  PHONE: {
    pattern: /^[0-9+\-\s()]{10,15}$/,
    maxLength: 15,
    minLength: 10
  },
  TEXT: {
    maxLength: 1000,
    minLength: 1,
    allowSpecialChars: true
  },
  NUMERIC: {
    pattern: /^[0-9]+$/,
    maxLength: 10
  },
  ALPHANUMERIC: {
    pattern: /^[a-zA-Z0-9]+$/,
    maxLength: 50
  }
};

// Dangerous patterns to detect and block
export const DANGEROUS_PATTERNS = {
  SQL_INJECTION: [
    /('|\\'|;|--|\/\*|\*\/|xp_|sp_|exec|execute|select|insert|update|delete|drop|create|alter|union|script)/i
  ],
  XSS_ATTACKS: [
    /<script[^>]*>.*?<\/script>/gi,
    /<iframe[^>]*>.*?<\/iframe>/gi,
    /<object[^>]*>.*?<\/object>/gi,
    /<embed[^>]*>.*?<\/embed>/gi,
    /<link[^>]*>.*?<\/link>/gi,
    /<meta[^>]*>.*?<\/meta>/gi,
    /javascript:/gi,
    /vbscript:/gi,
    /onload/gi,
    /onerror/gi,
    /onclick/gi,
    /onmouseover/gi
  ],
  COMMAND_INJECTION: [
    /[;&|`$(){}[\]]/,
    /(rm\s|del\s|format\s|shutdown|reboot)/i,
    /(wget|curl|nc|netcat)/i
  ],
  PATH_TRAVERSAL: [
    /\.\.\//,
    /\.\.\\/,
    /%2e%2e%2f/i,
    /%2e%2e%5c/i
  ]
};

class InputValidator {
  constructor() {
    this.isInitialized = false;
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    console.log('🛡️ Input Validator initialized');
  }

  /**
   * Sanitize input by removing dangerous characters and patterns
   */
  sanitize(input, options = {}) {
    if (typeof input !== 'string') {
      return input;
    }

    let sanitized = input;

    // Remove null bytes
    sanitized = sanitized.replace(/\0/g, '');

    // Remove control characters except newlines and tabs
    sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

    // Trim whitespace
    sanitized = sanitized.trim();

    // Remove excessive whitespace
    sanitized = sanitized.replace(/\s+/g, ' ');

    // Apply specific sanitization based on options
    if (options.removeHtml) {
      sanitized = this.removeHtmlTags(sanitized);
    }

    if (options.escapeHtml) {
      sanitized = this.escapeHtml(sanitized);
    }

    if (options.normalizeUnicode) {
      sanitized = this.normalizeUnicode(sanitized);
    }

    return sanitized;
  }

  /**
   * Remove HTML tags from input
   */
  removeHtmlTags(input) {
    return input.replace(/<[^>]*>/g, '');
  }

  /**
   * Escape HTML special characters
   */
  escapeHtml(input) {
    const htmlEscapes = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '/': '&#x2F;'
    };
    
    return input.replace(/[&<>"'/]/g, (match) => htmlEscapes[match]);
  }

  /**
   * Normalize Unicode characters
   */
  normalizeUnicode(input) {
    return input.normalize('NFC');
  }

  /**
   * Validate input against specific rules
   */
  validate(input, ruleType, options = {}) {
    const result = {
      isValid: true,
      sanitized: input,
      errors: [],
      warnings: []
    };

    if (typeof input !== 'string') {
      result.isValid = false;
      result.errors.push('Input must be a string');
      return result;
    }

    // Sanitize input first
    result.sanitized = this.sanitize(input, options);

    // Get validation rules
    const rules = VALIDATION_RULES[ruleType];
    if (!rules) {
      result.isValid = false;
      result.errors.push(`Unknown validation rule: ${ruleType}`);
      return result;
    }

    // Check length constraints
    if (rules.minLength && result.sanitized.length < rules.minLength) {
      result.isValid = false;
      result.errors.push(`Input must be at least ${rules.minLength} characters long`);
    }

    if (rules.maxLength && result.sanitized.length > rules.maxLength) {
      result.isValid = false;
      result.errors.push(`Input must be no more than ${rules.maxLength} characters long`);
    }

    // Check pattern matching
    if (rules.pattern && !rules.pattern.test(result.sanitized)) {
      result.isValid = false;
      result.errors.push(`Input does not match required format`);
    }

    // Check for dangerous patterns
    const dangerousPatterns = this.detectDangerousPatterns(result.sanitized);
    if (dangerousPatterns.length > 0) {
      result.isValid = false;
      result.errors.push(`Input contains potentially dangerous patterns: ${dangerousPatterns.join(', ')}`);
    }

    return result;
  }

  /**
   * Detect dangerous patterns in input
   */
  detectDangerousPatterns(input) {
    const detected = [];

    // Check SQL injection patterns
    for (const pattern of DANGEROUS_PATTERNS.SQL_INJECTION) {
      if (pattern.test(input)) {
        detected.push('SQL_INJECTION');
        break;
      }
    }

    // Check XSS patterns
    for (const pattern of DANGEROUS_PATTERNS.XSS_ATTACKS) {
      if (pattern.test(input)) {
        detected.push('XSS_ATTACK');
        break;
      }
    }

    // Check command injection patterns
    for (const pattern of DANGEROUS_PATTERNS.COMMAND_INJECTION) {
      if (pattern.test(input)) {
        detected.push('COMMAND_INJECTION');
        break;
      }
    }

    // Check path traversal patterns
    for (const pattern of DANGEROUS_PATTERNS.PATH_TRAVERSAL) {
      if (pattern.test(input)) {
        detected.push('PATH_TRAVERSAL');
        break;
      }
    }

    return detected;
  }

  /**
   * Validate and sanitize email input
   */
  async validateEmail(email, options = {}) {
    const result = this.validate(email, 'EMAIL', options);
    
    if (!result.isValid) {
      await this.logValidationFailure('EMAIL', email, result.errors);
    }
    
    return result;
  }

  /**
   * Validate and sanitize password input
   */
  async validatePassword(password, options = {}) {
    const result = this.validate(password, 'PASSWORD', options);
    
    if (!result.isValid) {
      await this.logValidationFailure('PASSWORD', password, result.errors);
    }
    
    return result;
  }

  /**
   * Validate and sanitize username input
   */
  async validateUsername(username, options = {}) {
    const result = this.validate(username, 'USERNAME', options);
    
    if (!result.isValid) {
      await this.logValidationFailure('USERNAME', username, result.errors);
    }
    
    return result;
  }

  /**
   * Validate and sanitize phone number input
   */
  async validatePhone(phone, options = {}) {
    const result = this.validate(phone, 'PHONE', options);
    
    if (!result.isValid) {
      await this.logValidationFailure('PHONE', phone, result.errors);
    }
    
    return result;
  }

  /**
   * Validate and sanitize text input
   */
  async validateText(text, options = {}) {
    const result = this.validate(text, 'TEXT', options);
    
    if (!result.isValid) {
      await this.logValidationFailure('TEXT', text, result.errors);
    }
    
    return result;
  }

  /**
   * Validate and sanitize numeric input
   */
  async validateNumeric(numeric, options = {}) {
    const result = this.validate(numeric, 'NUMERIC', options);
    
    if (!result.isValid) {
      await this.logValidationFailure('NUMERIC', numeric, result.errors);
    }
    
    return result;
  }

  /**
   * Log validation failures for security monitoring
   */
  async logValidationFailure(ruleType, input, errors) {
    try {
      await logSecurityEvent(EVENT_TYPES.INPUT_VALIDATION_FAILED, null, {
        ruleType,
        input: input.substring(0, 50) + (input.length > 50 ? '...' : ''), // Truncate for security
        errors,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log validation failure:', error);
    }
  }

  /**
   * Log injection attempts
   */
  async logInjectionAttempt(input, patternType) {
    try {
      await logSecurityEvent(EVENT_TYPES.INJECTION_ATTEMPT, null, {
        patternType,
        input: input.substring(0, 100) + (input.length > 100 ? '...' : ''),
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log injection attempt:', error);
    }
  }

  /**
   * Validate form data
   */
  async validateForm(formData, validationRules) {
    const results = {};
    const isValid = true;

    for (const [field, rules] of Object.entries(validationRules)) {
      const value = formData[field];
      if (value !== undefined && value !== null) {
        const result = await this.validate(value, rules.type, rules.options || {});
        results[field] = result;
        if (!result.isValid) {
          isValid = false;
        }
      }
    }

    return {
      isValid,
      results,
      sanitizedData: Object.fromEntries(
        Object.entries(results).map(([field, result]) => [field, result.sanitized])
      )
    };
  }
}

// Create singleton instance
const inputValidator = new InputValidator();

// Export functions for easy use
export const sanitizeInput = (input, options) => inputValidator.sanitize(input, options);
export const validateInput = (input, ruleType, options) => inputValidator.validate(input, ruleType, options);
export const validateEmail = (email, options) => inputValidator.validateEmail(email, options);
export const validatePassword = (password, options) => inputValidator.validatePassword(password, options);
export const validateUsername = (username, options) => inputValidator.validateUsername(username, options);
export const validatePhone = (phone, options) => inputValidator.validatePhone(phone, options);
export const validateText = (text, options) => inputValidator.validateText(text, options);
export const validateNumeric = (numeric, options) => inputValidator.validateNumeric(numeric, options);
export const validateForm = (formData, validationRules) => inputValidator.validateForm(formData, validationRules);
export const detectDangerousPatterns = (input) => inputValidator.detectDangerousPatterns(input);

// Export the class for advanced usage
export default inputValidator;
